import {
  CheckCircleIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export interface RsvpCounts {
  yes: number;
  maybe: number;
  no: number;
  not_responded: number;
}

const RING_RADIUS = 26;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * RSVP summary: an SVG ring (going / maybe / can't arcs over a gray track)
 * with a "N going" center label and an icon legend.
 */
export default function RsvpSummary({ counts }: { counts: RsvpCounts }) {
  const total = counts.yes + counts.maybe + counts.no + counts.not_responded;

  const arc = (count: number) => (total > 0 ? (count / total) * RING_CIRCUMFERENCE : 0);
  const yesArc = arc(counts.yes);
  const maybeArc = arc(counts.maybe);
  const noArc = arc(counts.no);

  const segments = [
    { length: yesArc, offset: 0, className: 'stroke-green-500' },
    { length: maybeArc, offset: yesArc, className: 'stroke-amber-400' },
    { length: noArc, offset: yesArc + maybeArc, className: 'stroke-red-400' },
  ].filter((s) => s.length > 0.5);

  return (
    <div className="flex items-center gap-4">
      <svg
        width="72"
        height="72"
        viewBox="0 0 64 64"
        role="img"
        aria-label={`${counts.yes} of ${total} going`}
        className="shrink-0"
      >
        <circle
          cx="32"
          cy="32"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="7"
          className="stroke-flock-gray-200"
        />
        {segments.map((s, i) => (
          <circle
            key={i}
            cx="32"
            cy="32"
            r={RING_RADIUS}
            fill="none"
            strokeWidth="7"
            strokeLinecap={segments.length > 1 ? 'butt' : 'round'}
            strokeDasharray={`${s.length} ${RING_CIRCUMFERENCE - s.length}`}
            strokeDashoffset={-s.offset}
            transform="rotate(-90 32 32)"
            className={s.className}
          />
        ))}
        <text
          x="32"
          y="31"
          textAnchor="middle"
          className="fill-flock-navy-900 text-[15px] font-bold"
        >
          {counts.yes}
        </text>
        <text x="32" y="43" textAnchor="middle" className="fill-flock-navy-400 text-[8px]">
          going
        </text>
      </svg>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium">
        <span className="flex items-center gap-1 text-green-700">
          <CheckCircleIcon className="h-4 w-4" /> {counts.yes} going
        </span>
        <span className="flex items-center gap-1 text-amber-700">
          <QuestionMarkCircleIcon className="h-4 w-4" /> {counts.maybe} maybe
        </span>
        <span className="flex items-center gap-1 text-red-700">
          <XCircleIcon className="h-4 w-4" /> {counts.no} can&apos;t
        </span>
        <span className="flex items-center gap-1 text-flock-navy-400">
          <ClockIcon className="h-4 w-4" /> {counts.not_responded} awaiting
        </span>
      </div>
    </div>
  );
}
